import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Wrapper from "../../../components/TenantWrapper";
import "./searchProperty.css";
import title from "../../../components/title";
import cardPic from "../../../assets/card_pic.webp";
import ImageSlider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Skeleton,
  Chip,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  Badge,
  IconButton,
  Paper,
  Avatar,
  LinearProgress,
  Alert,
  Tooltip,
  Popover,
  Stack,
} from "@mui/material";

import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BedIcon from "@mui/icons-material/Bed";
import PetsIcon from "@mui/icons-material/Pets";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"; // ← NEW
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { BASE_URL } from "../../../config/config";
import FlatDetailModal from "./FlatDetailModal";
import InterestRequestForm from "./InterestRequestForm";
import { GetTenantInterestRequests } from "../../../redux/reducer/FlatInterestRequest";
import WifiIcon from "@mui/icons-material/Wifi";
import { RulesBadge } from "../../../components/PropertyRulesPanel";
import { usePropertyRules } from "../../../utils/propertyRules";
import {
  evaluateTenantEligibility,
  normalizeAllowedFor,
  allowedForLabel,
} from "../../../utils/tenantEligibility";
import BlockIcon from "@mui/icons-material/Block";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { isPastDueDate } from "../../../utils/dueDate";
import PropertyRatingBadge from "../../../components/PropertyRatingBadge";

// ------------------- NEW: Bill Uploaded By Badge --------------------
// const BillUploadedByBadge = ({ billUploadedBy }) => {
//   if (!billUploadedBy) return null;

//   const val = billUploadedBy?.toLowerCase();
//   const isOwner = val === "owner" || val === "landlord";
//   const isTenant = val === "tenant";

//   if (!isOwner && !isTenant) return null;

//   return (
//     <Tooltip
//       title={
//         isOwner
//           ? "Owner will upload the utility bill each month"
//           : "Tenant is responsible for uploading the utility bill each month"
//       }
//       arrow
//       placement="top"
//     >
//       <Box
//         sx={{
//           display: "inline-flex",
//           alignItems: "center",
//           gap: 0.6,
//           px: 1.2,
//           py: 0.5,
//           mt: 1,
//           borderRadius: "8px",
//           fontSize: "11px",
//           fontWeight: 700,
//           letterSpacing: "0.3px",
//           cursor: "default",
//           userSelect: "none",
//           ...(isOwner
//             ? {
//                 bgcolor: "rgba(16, 185, 129, 0.1)",
//                 color: "#065f46",
//                 border: "1.5px solid rgba(16, 185, 129, 0.4)",
//               }
//             : {
//                 bgcolor: "rgba(59, 130, 246, 0.1)",
//                 color: "#1e3a5f",
//                 border: "1.5px solid rgba(59, 130, 246, 0.4)",
//               }),
//         }}
//       >
//         <ReceiptLongIcon sx={{ fontSize: 13 }} />
//         <span>
//           Bill: <strong>{isOwner ? "Owner Uploads" : "Tenant Uploads"}</strong>
//         </span>
//       </Box>
//     </Tooltip>
//   );
// };
// ----------------------------------------------------------------

// ------------------- API Call with Filters --------------------
const searchProperties = async (query, filters = {}) => {
  // Build query params
  const params = new URLSearchParams();
  if (query.includes("availability=all")) filters = {};
  query = query.replace("?availability=all", "");
  if (query) params.append("q", query);
  if (filters.city) params.append("city", filters.city);
  if (
    filters.rentRange &&
    (filters.rentRange[0] > 0 || filters.rentRange[1] < 100000)
  ) {
    params.append("minRent", filters.rentRange[0]);
    params.append("maxRent", filters.rentRange[1]);
  }
  if (filters.bedrooms) params.append("bedrooms", filters.bedrooms);
  if (filters.allowedFor) params.append("allowedFor", filters.allowedFor);
  if (filters.petsAllowed) params.append("petsAllowed", "true");
  if (filters.floor) params.append("floor", filters.floor);
  if (filters.availability) params.append("availability", filters.availability);
  const token = sessionStorage.getItem("ots_token");
  const response = await fetch(
    `${BASE_URL}/find-property?${params.toString()}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.json();
};

// ------------------- API Call for Recommended Properties --------------------
const getRecommendedProperties = async (tenantId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/recommended-properties/${tenantId}`
    );
    return response.data;
  } catch (error) {
    return { data: [] };
  }
};

// ------------------- Calculate Match Percentage --------------------
const calculateMatchPercentage = (property, tenantProfile) => {
  if (!tenantProfile) return 0;

  let score = 0;
  let totalFactors = 0;

  // Budget match (most important - 30%)
  const rent = Number(property.flatRent) || 0;
  if (tenantProfile.budgetMin && tenantProfile.budgetMax) {
    totalFactors += 30;
    if (rent >= tenantProfile.budgetMin && rent <= tenantProfile.budgetMax) {
      score += 30;
    } else if (rent <= tenantProfile.budgetMax * 1.1) {
      score += 15; // Slightly over budget
    }
  }

  // Tenant type match (25%)
  if (tenantProfile.tenantType) {
    totalFactors += 25;
    const allowedFor = property.allowedFor;
    const tenantType =
      tenantProfile.tenantType === "bachelors" ? "bachelor" : "family";
    if (allowedFor === tenantType || allowedFor === "both") {
      score += 25;
    }
  }
  // Pets match (15%)
  if (tenantProfile.hasPets) {
    totalFactors += 15;
    if (property.petAllowed === "true" || property.petAllowed === true) {
      score += 15;
    }
  }
  // Parking match (15%)
  if (tenantProfile.hasVehicle) {
    totalFactors += 15;
    if (
      property.parkingAvailable === "true" ||
      property.parkingAvailable === true
    ) {
      score += 15;
    }
  }
  // Bedrooms match (15%)
  if (tenantProfile.totalFamilyMembers) {
    totalFactors += 15;
    const rooms = Number(property.flatRooms) || 1;
    const neededRooms = Math.ceil(tenantProfile.totalFamilyMembers / 2);
    if (rooms >= neededRooms) {
      score += 15;
    } else if (rooms === neededRooms - 1) {
      score += 8;
    }
  }
  return totalFactors > 0 ? Math.round((score / totalFactors) * 100) : 85;
};
// ----------------------------------------------------------------

// Default filter state
const defaultFilters = {
  city: "",
  rentRange: [0, 100000],
  bedrooms: "",
  allowedFor: "",
  petsAllowed: false,
  floor: "",
  availability: "available",
};

// Helper function to parse filters from URL params
const getFiltersFromParams = (searchParams) => {
  return {
    city: searchParams.get("city") || "",
    rentRange: [
      Number(searchParams.get("minRent")) || 0,
      Number(searchParams.get("maxRent")) || 100000,
    ],
    bedrooms: searchParams.get("bedrooms") || "",
    allowedFor: searchParams.get("allowedFor") || "",
    petsAllowed: searchParams.get("petsAllowed") === "true",
    floor: searchParams.get("floor") || "",
    availability: searchParams.get("availability") || "available",
  };
};

// Helper function to update URL params from filters
const updateUrlParams = (searchParams, setSearchParams, search, filters) => {
  const params = new URLSearchParams();

  if (search) params.set("q", search);
  if (filters.city) params.set("city", filters.city);
  if (filters.rentRange[0] > 0) params.set("minRent", filters.rentRange[0]);
  if (filters.rentRange[1] < 100000)
    params.set("maxRent", filters.rentRange[1]);
  if (filters.bedrooms) params.set("bedrooms", filters.bedrooms);
  if (filters.allowedFor) params.set("allowedFor", filters.allowedFor);
  if (filters.petsAllowed) params.set("petsAllowed", "true");
  if (filters.floor) params.set("floor", filters.floor);
  if (filters.availability && filters.availability !== "available") {
    params.set("availability", filters.availability);
  }

  setSearchParams(params, { replace: true });
};

export default function SearchProperty() {
  title("Search Property");

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const open = useSelector((state) => state.sidebar.open);
  const myRequests = useSelector(
    (state) => state.flatInterest?.myRequests || []
  );
  const { validateUser } = useSelector((state) => state);

  // Get tenant profile from Redux - memoized to prevent useEffect from running on every render
  const tenantProfile = useMemo(
    () => ({
      ...validateUser?.UserValidate?.data?.user,
      ...validateUser?.UserValidate?.data?.tenantProfile,
    }),
    [
      validateUser?.UserValidate?.data?.user,
      validateUser?.UserValidate?.data?.tenantProfile,
    ]
  );
  const tenantId = validateUser?.UserValidate?.data?.user?._id;

  // Load search and filters from URL params
  const initialSearch = searchParams.get("q") || "";
  const initialFilters = getFiltersFromParams(searchParams);

  const [search, setSearch] = useState(initialSearch);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  // Anchors for the inline filter popovers (Price Range / More Filters) —
  // filters apply instantly on change, so there's no drawer or draft state.
  const [priceAnchorEl, setPriceAnchorEl] = useState(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);

  // Recommended properties state
  const [recommendedProperties, setRecommendedProperties] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(true);
  const [showRecommended, setShowRecommended] = useState(true);
  const [filtersAppliedFromProfile, setFiltersAppliedFromProfile] =
    useState(false);

  // Filter States - applied instantly, the debounced search effect below
  // picks up every change (600ms) so there's no separate draft/apply step.
  const [filters, setFilters] = useState(initialFilters);

  // Modal States for Flat Details and Interest Form
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [interestFormOpen, setInterestFormOpen] = useState(false);
  const [selectedFlat, setSelectedFlat] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedLandlord, setSelectedLandlord] = useState({
    id: "",
    name: "",
    email: "",
    phone: "",
  });

  const handleChildSubmitSuccess = () => {
    setRefreshKey((prev) => prev + 1); // trigger re-fetch or re-render
  };

  // ------------- Filter Logic ------------------
  const applyFilters = (properties) => {
    // Safety check - ensure properties is an array
    if (!properties || !Array.isArray(properties)) {
      return [];
    }
    return properties.filter((item) => {
      // Extract flat data
      const flats = item.property ? [item.property] : item.flats || [];

      // Filter each flat
      const matchingFlats = flats.filter((flat) => {
        // City filter
        if (filters.city && flat.city !== filters.city) return false;

        // Rent range filter
        const rent = Number(flat.flatRent) || 0;
        if (rent < filters.rentRange[0] || rent > filters.rentRange[1])
          return false;

        // Bedrooms filter
        if (
          filters.bedrooms &&
          Number(flat.flatRooms) !== Number(filters.bedrooms)
        )
          return false;

        // Family/Bachelor filter — a property open to "both" satisfies either
        // choice, so it must never be filtered out of a family/bachelor search.
        if (filters.allowedFor) {
          const allowedFor = normalizeAllowedFor(flat.allowedFor);
          const wanted = normalizeAllowedFor(filters.allowedFor);
          if (wanted !== "both" && allowedFor !== "both" && allowedFor !== wanted)
            return false;
        }

        // Pets allowed filter
        if (
          filters.petsAllowed &&
          flat.petAllowed !== "true" &&
          flat.petAllowed !== true
        )
          return false;

        // Floor filter
        if (filters.floor && Number(flat.flatFloor) !== Number(filters.floor))
          return false;


        // removing this filter now lets the user see rented properties too
        // Availability filter
        // if (
        //   filters.availability === "available" &&
        //   (Number(flat.is_rent) === 1 || !flat.is_approved)
        // )
        //   return false;

        return true;
      });

      // Keep item if it has matching flats
      if (item.property) {
        return matchingFlats.length > 0;
      } else if (item.flats) {
        item.flats = matchingFlats;
        return matchingFlats.length > 0;
      }
      return false;
    });
  };

  // ------------- Debounce Search Logic ------------------
  const debounceSearch = useCallback((value, currentFilters) => {
    setLoading(true);

    searchProperties(value, currentFilters)
      .then((result) => {
        // Handle both array response and object response { success, data }
        if (Array.isArray(result)) {
          setData(result);
        } else if (result && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        setData([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      // Search if there's a query OR if filters are applied
      const hasFilters =
        filters.city ||
        filters.bedrooms ||
        filters.allowedFor ||
        filters.petsAllowed ||
        filters.floor ||
        filters.rentRange[0] > 0 ||
        filters.rentRange[1] < 100000 ||
        filters.availability !== "available";

      if (search.trim() !== "" || hasFilters) {
        debounceSearch(search, filters);
      } else {
        setData([]);
      }
    }, 600);

    return () => clearTimeout(timeout);
  }, [search, filters, debounceSearch]);
  // -------------------------------------------------------

  // Update URL params whenever search or filters change
  useEffect(() => {
    updateUrlParams(searchParams, setSearchParams, search, filters);
  }, [search, filters, searchParams, setSearchParams]);

  // Fetch tenant's existing requests on page load, and again whenever the
  // tenant comes back to this tab — a request's status (e.g. the owner
  // accepting it) changes on the server while this tab sits in the
  // background, so "Pay Now" needs a fresh fetch to show up without the
  // tenant having to log out and back in.
  //
  // tenantId here must match what InterestRequestForm actually sends the
  // request under (Redux's validateUser first, localStorage as fallback) —
  // fetching under a different id than the request was saved with meant a
  // just-sent request could never be found here, no matter how often this
  // refetched.
  useEffect(() => {
    const tenantIdLocal =
      tenantId ||
      localStorage.getItem("user_id") ||
      localStorage.getItem("userId");
    if (!tenantIdLocal) return;

    dispatch(GetTenantInterestRequests({ tenantId: tenantIdLocal }));

    const onFocus = () => {
      if (document.visibilityState === "visible") {
        dispatch(GetTenantInterestRequests({ tenantId: tenantIdLocal }));
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [dispatch, refreshKey, tenantId]);

  // Fetch recommended properties based on tenant profile
  useEffect(() => {
    const fetchRecommendedProperties = async () => {
      if (!tenantId) {
        setRecommendedLoading(false);
        return;
      }

      setRecommendedLoading(true);
      try {
        const result = await getRecommendedProperties(tenantId);
        const properties = result?.data || result || [];
        setRecommendedProperties(Array.isArray(properties) ? properties : []);
      } catch (error) {
        setRecommendedProperties([]);
      }
      setRecommendedLoading(false);
    };

    fetchRecommendedProperties();
  }, [tenantId, refreshKey]);

  // Apply filters from tenant profile on first load (if no URL params)
  useEffect(() => {
    if (filtersAppliedFromProfile || !tenantProfile) return;

    // Only apply profile filters if no filters are in URL
    const hasUrlFilters = searchParams.toString().length > 0;
    if (hasUrlFilters) {
      setFiltersAppliedFromProfile(true);
      return;
    }

    const profileBasedFilters = { ...defaultFilters };

    // Apply budget range from profile
    if (tenantProfile.budgetMin || tenantProfile.budgetMax) {
      profileBasedFilters.rentRange = [
        tenantProfile.budgetMin || 0,
        tenantProfile.budgetMax || 100000,
      ];
    }

    // Tenant type and pets are deliberately NOT pre-applied as filters. A
    // family-only or no-pets property must stay visible to a bachelor / pet
    // owner — they simply cannot request it, and the card says exactly why.
    // Both filters remain available by hand in the inline filter bar.

    setFilters(profileBasedFilters);
    setFiltersAppliedFromProfile(true);
  }, [tenantProfile, filtersAppliedFromProfile, searchParams]);

  // Get profile preferences summary for display
  const getProfileSummary = () => {
    const summary = [];
    if (tenantProfile.budgetMin || tenantProfile.budgetMax) {
      summary.push({
        icon: <PaymentIcon fontSize="small" />,
        label: `PKR ${(tenantProfile.budgetMin || 0).toLocaleString()} - ${(
          tenantProfile.budgetMax || 100000
        ).toLocaleString()}`,
      });
    }
    if (tenantProfile.tenantType) {
      summary.push({
        icon:
          tenantProfile.tenantType === "family" ? (
            <FamilyRestroomIcon fontSize="small" />
          ) : (
            <PersonIcon fontSize="small" />
          ),
        label: tenantProfile.tenantType === "family" ? "Family" : "Bachelor",
      });
    }
    if (tenantProfile.hasPets) {
      summary.push({
        icon: <PetsIcon fontSize="small" />,
        label: "Pet Friendly",
      });
    }
    if (tenantProfile.hasVehicle) {
      summary.push({
        icon: <DirectionsCarIcon fontSize="small" />,
        label: "Parking Needed",
      });
    }
    return summary;
  };

  // Helper function to get request for a flat
  const getRequestForFlat = (flatId) => {
    if (!flatId || !myRequests.length) return null;
    return myRequests.find(
      (req) => req.flatId === flatId || req.flat_id === flatId
    );
  };

  // Helper function to get request status for a flat
  const getRequestStatusForFlat = (flatId) => {
    const request = getRequestForFlat(flatId);
    return request ? request.status : null;
  };

  // Owner's property data vs this tenant's profile — same rule-set the detail
  // modal and the request form use, so a card can never promise something the
  // modal then refuses.
  const eligibilityFor = (flat) =>
    evaluateTenantEligibility(flat, tenantProfile);

  const tooltipProps = {
    tooltip: {
      sx: {
        whiteSpace: "pre-line",
        maxWidth: 320,
        fontSize: "0.78rem",
        lineHeight: 1.5,
        p: 1.5,
      },
    },
  };

  // One chip, one meaning: what the owner allows, coloured by whether it
  // matches this tenant. The old copy labelled an "open to both" property as
  // "Bachelors Allowed" and painted every bachelor property red.
  const renderAllowedForChip = (flat) => {
    const label = allowedForLabel(flat?.allowedFor);
    if (!label) return null;

    const mismatch = eligibilityFor(flat).blockers.some(
      (rule) => rule.key === "tenantType"
    );

    return (
      <Chip
        label={`${label} Allowed`}
        color={mismatch ? "error" : "success"}
        size="small"
      />
    );
  };

  // Ribbon on the card itself: the tenant sees the mismatch before opening the
  // property. "Already rented" is left out — the status chip above already says
  // it, and repeating it reads as two different problems.
  const renderEligibilityChip = (flat) => {
    const eligibility = eligibilityFor(flat);
    const profileBlockers = eligibility.blockers.filter(
      (rule) => rule.key !== "rented"
    );
    if (!profileBlockers.length) return null;

    const label =
      profileBlockers.length === 1
        ? profileBlockers[0].title
        : `${profileBlockers.length} mismatches`;

    return (
      <Tooltip
        title={eligibility.tooltip}
        placement="right"
        arrow
        componentsProps={tooltipProps}
      >
        <Chip
          icon={<BlockIcon />}
          label={label}
          size="small"
          sx={{
            bgcolor: "rgba(211, 47, 47, 0.92)",
            color: "#fff",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#fff" },
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    );
  };

  // Helper function to render status chip for card header
  const renderStatusChip = (flat) => {
    const flatId = flat?._id || flat?.id || flat?.flatId;
    const request = getRequestForFlat(flatId);

    // paymentStatus: "verified" = landlord verified, "verifying" = waiting for landlord
    if (request?.paymentStatus === "verified") {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Payment Verified"
          color="success"
          size="small"
        />
      );
    }
    if (request?.paymentStatus === "verifying") {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="Verifying Payment"
          color="warning"
          size="small"
        />
      );
    }
    if (request?.paymentStatus === "failed") {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Payment Failed"
          color="error"
          size="small"
        />
      );
    }

    const status = request?.status;
    if (status === "pending") {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="Pending"
          color="warning"
          size="small"
        />
      );
    } else if (status === "accepted") {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Accepted"
          color="info"
          size="small"
        />
      );
    } else if (status === "payment_pending") {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="Verifying Payment"
          color="warning"
          size="small"
        />
      );
    } else if (status === "completed") {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Confirmed"
          color="success"
          size="small"
        />
      );
    } else if (status === "rejected" || status === "payment_rejected") {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Rejected"
          color="error"
          size="small"
        />
      );
    }
    if (Number(flat?.is_rent) === 1) {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Rented"
          color="error"
          size="small"
        />
      );
    }
    return <Chip label="Available" color="success" size="small" />;
  };

  // Helper function to render action button based on status
  const renderActionButton = (flat, landlordInfo) => {
    const flatId = flat._id || flat.id || flat.flatId;
    const request = getRequestForFlat(flatId);
    const status = request?.status;

    // If payment is verified by landlord
    if (request?.paymentStatus === "verified") {
      return (
        <Button
          variant="contained"
          size="small"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/my-requests?flatId=${flatId}`;
          }}
        >
          Confirmed
        </Button>
      );
    }

    // If payment is being verified (waiting for landlord)
    if (request?.paymentStatus === "verifying") {
      return (
        <Button
          variant="outlined"
          size="small"
          color="warning"
          startIcon={<HourglassEmptyIcon />}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/my-requests?flatId=${flatId}`;
          }}
        >
          Verifying
        </Button>
      );
    }

    // If payment failed
    if (request?.paymentStatus === "failed") {
      return (
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<CancelIcon />}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/my-requests?flatId=${flatId}`;
          }}
        >
          Failed
        </Button>
      );
    }

    // "accepted" or "expired" (missed the deadline once, may still have an
    // extension chance) with nothing paid yet — same condition RequestCard.js
    // uses on My Requests, kept in sync so the two screens never disagree.
    if (
      (status === "accepted" || status === "expired") &&
      (request?.paymentStatus === "unpaid" || request?.paymentStatus === "failed")
    ) {
      const overdue = isPastDueDate(request?.dueDate);
      return (
        <Button
          variant="contained"
          size="small"
          color={overdue ? "warning" : "success"}
          startIcon={overdue ? <ErrorOutlineIcon /> : <PaymentIcon />}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = `/my-requests?flatId=${flatId}`;
          }}
        >
          {overdue ? "Payment Overdue" : "Pay Now"}
        </Button>
      );
    }

    // Another applicant for this flat is being processed first — nothing
    // for this tenant to do but wait.
    if (status === "hold") {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="On Hold"
          size="small"
          color="default"
        />
      );
    }

    // No request yet: if the tenant's profile clashes with what the owner
    // registered, show the request action as disabled with the reason on hover
    // instead of letting them find out only after opening the property.
    if (!status) {
      const eligibility = eligibilityFor(flat);
      if (!eligibility.canRequest) {
        return (
          <Tooltip
            title={eligibility.tooltip}
            placement="top"
            arrow
            componentsProps={tooltipProps}
          >
            <span
              style={{ display: "inline-flex" }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<BlockIcon />}
                disabled
                sx={{
                  textTransform: "none",
                  "&.Mui-disabled": {
                    borderColor: "rgba(211, 47, 47, 0.4)",
                    color: "rgba(211, 47, 47, 0.8)",
                  },
                }}
              >
                Can't Request
              </Button>
            </span>
          </Tooltip>
        );
      }
    }

    // No button for default state - card click will handle it
    return null;
  };

  // Handle filter changes (only updates temporary filters)
  // Applies immediately — the debounced search effect re-fetches on every
  // change, so picking an option is the whole interaction (no "Apply" step).
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Reset filters and clear URL params
  const handleResetFilters = () => {
    setFilters({ ...defaultFilters });
    setSearch("");
  };

  // Handle flat card click - open detail modal
  const handleFlatClick = (flat, landlordInfo) => {
    setSelectedFlat(flat);
    setSelectedLandlord({
      id:
        landlordInfo.id ||
        landlordInfo.landlordId ||
        landlordInfo._id ||
        landlordInfo.landlord_id ||
        "",
      name:
        landlordInfo.name ||
        landlordInfo.landlordName ||
        landlordInfo.landlord_name ||
        "",
      email:
        landlordInfo.email ||
        landlordInfo.landlordEmail ||
        landlordInfo.landlord_email ||
        "",
      phone:
        landlordInfo.phone ||
        landlordInfo.landlordPhone ||
        landlordInfo.landlord_phone ||
        "",
    });
    setDetailModalOpen(true);
  };

  // Handle "I'm Interested" button click from detail modal
  const handleInterested = ({
    flat,
    landlordId,
    landlordName,
    landlordEmail,
  }) => {
    setDetailModalOpen(false);
    setSelectedFlat(flat);
    setSelectedLandlord({
      id: landlordId,
      name: landlordName,
      email: landlordEmail,
      phone: "",
    });
    setInterestFormOpen(true);
  };

  // Close modals
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedFlat(null);
  };

  const handleCloseInterestForm = () => {
    setInterestFormOpen(false);
  };

  // Apply filters to data
  const filteredData = applyFilters(data);

  // A flat's exit terms are stored against its landlord, not the flat, so the
  // lookup needs both ids paired up before the rule-sets can be fetched.
  const rulesLookupFlats = useMemo(() => {
    const pairs = [];
    const add = (flat, landlordId) => {
      const flatId = flat?._id || flat?.id || flat?.flatId;
      const ownerId = landlordId || flat?.landlordId || flat?.userId;
      if (flatId && ownerId) pairs.push({ _id: flatId, landlordId: ownerId });
    };

    (Array.isArray(data) ? data : []).forEach((item) => {
      const ownerId =
        item.landlordId ||
        item.landlord_id ||
        item.userId ||
        item.user_id ||
        item.ownerId ||
        item._id;
      if (item.property) add(item.property, ownerId);
      (item.flats || []).forEach((flat) => add(flat, ownerId));
    });

    (Array.isArray(recommendedProperties) ? recommendedProperties : []).forEach(
      (item) => {
        const flat = item.property || item;
        add(flat, item.landlordId || item.landlord_id || item.ownerId);
      }
    );

    return pairs;
  }, [data, recommendedProperties]);

  const { rulesByProperty } = usePropertyRules(rulesLookupFlats);

  const rulesForFlat = (flat) =>
    rulesByProperty.get(String(flat?._id || flat?.id || flat?.flatId)) || null;

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.city) count++;
    if (filters.rentRange[0] > 0 || filters.rentRange[1] < 100000) count++;
    if (filters.bedrooms) count++;
    if (filters.allowedFor) count++;
    if (filters.petsAllowed) count++;
    if (filters.floor) count++;
    if (filters.availability !== "available") count++;
    return count;
  };

  // Image Slider settings
  const CustomArrow = ({ onClick, direction }) => (
    <div
      className={`custom-arrow ${direction}`}
      onClick={onClick}
      role="button"
    />
  );

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: true,
    autoplaySpeed: 4000,
    nextArrow: <CustomArrow direction="next" />,
    prevArrow: <CustomArrow direction="prev" />,
  };

  // Helper function for ordinal numbers (1st, 2nd, 3rd, etc.)
  // const getOrdinal = (num) => {
  //   if(num === 0) return "Ground";
  //   if (num % 100 >= 11 && num % 100 <= 13) {
  //     return `${num}th`;
  //   }
  //   switch (num % 10) {
  //     case 1:
  //       return `${num}st`;
  //     case 2:
  //       return `${num}nd`;
  //     case 3:
  //       return `${num}rd`;
  //     default:
  //       return `${num}th`;
  //   }
  // };
  const getOrdinal = (num) => {
    if (num === 0) return "Ground";
    if (num % 100 >= 11 && num % 100 <= 13) {
      return `${num}th`;
    }
    switch (num % 10) {
      case 1:
        return `${num}st`;
      case 2:
        return `${num}nd`;
      case 3:
        return `${num}rd`;
      default:
        return `${num}th`;
    }
  };

  const renderSkeletons = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <Grid item key={index} xs={12} sm={6} md={4} lg={3.5} xl={2.3}>
        <Card sx={{ maxWidth: 350, borderRadius: 2 }}>
          <Skeleton variant="rectangular" height={200} />
          <CardContent sx={{ p: 2 }}>
            <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton width="80%" height={28} sx={{ mb: 0.5 }} />
            <Skeleton width="50%" height={22} sx={{ mb: 1 }} />
            <Skeleton width="70%" height={20} sx={{ mb: 1 }} />
            <Skeleton width="40%" height={24} sx={{ mb: 1 }} />
            <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Skeleton width={80} height={28} sx={{ borderRadius: "10px" }} />
              <Skeleton width={80} height={28} sx={{ borderRadius: "10px" }} />
            </Box>
            <Skeleton width="50%" height={20} />
          </CardContent>
        </Card>
      </Grid>
    ));
  };

  return (
    <>
      <Wrapper />

      <div className={`${open ? "sidebar-open" : "sidebar-closed"} `}>
        <div className="mainHeading">
          <h1>Find Property</h1>
        </div>

        {/* ----------- Search Box ------------ */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        {/* ----------- Inline Filter Bar -------------
            Every control writes straight into `filters`, so a click/selection
            IS the search — no drawer, no draft state, no Apply button. The
            existing 600ms debounce on `filters` (see the search effect above)
            still gates the actual API calls, so this only changes how the
            same filters are presented, not when they fire. */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            mb: 3,
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.15)",
              borderRadius: 3,
            },
          }}
        >
          {/* Bedrooms */}
          <FormControl
            size="small"
            sx={{ minWidth: 148, flexShrink: 0 }}
            variant="outlined"
          >
            <InputLabel id="bedrooms-filter-label">Bedrooms</InputLabel>
            <Select
              labelId="bedrooms-filter-label"
              id="bedrooms-filter"
              value={filters.bedrooms}
              label="Bedrooms"
              onChange={(e) => handleFilterChange("bedrooms", e.target.value)}
              sx={{
                borderRadius: 5,
                bgcolor: filters.bedrooms ? "rgba(17,153,142,0.08)" : "transparent",
              }}
            >
              <MenuItem value="">Any Bedrooms</MenuItem>
              <MenuItem value={1}>1 Bedroom</MenuItem>
              <MenuItem value={2}>2 Bedrooms</MenuItem>
              <MenuItem value={3}>3 Bedrooms</MenuItem>
              <MenuItem value={4}>4 Bedrooms</MenuItem>
              <MenuItem value={5}>5+ Bedrooms</MenuItem>
            </Select>
          </FormControl>

          {/* Family/Bachelor Filter */}
          <FormControl
            size="small"
            sx={{ minWidth: 140, flexShrink: 0 }}
            variant="outlined"
          >
            <InputLabel id="allowedFor-filter-label">Tenant Type</InputLabel>
            <Select
              labelId="allowedFor-filter-label"
              id="allowedFor-filter"
              value={filters.allowedFor}
              label="Tenant Type"
              onChange={(e) =>
                handleFilterChange("allowedFor", e.target.value)
              }
              sx={{
                borderRadius: 5,
                bgcolor: filters.allowedFor ? "rgba(17,153,142,0.08)" : "transparent",
              }}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="family">Family</MenuItem>
              <MenuItem value="bachelor">Bachelor</MenuItem>
            </Select>
          </FormControl>

          {/* Price Range - a popover keeps the two-handle slider off the bar */}
          <Chip
            clickable
            variant={
              filters.rentRange[0] > 0 || filters.rentRange[1] < 100000
                ? "filled"
                : "outlined"
            }
            color={
              filters.rentRange[0] > 0 || filters.rentRange[1] < 100000
                ? "primary"
                : "default"
            }
            onClick={(e) => setPriceAnchorEl(e.currentTarget)}
            label={
              filters.rentRange[0] > 0 || filters.rentRange[1] < 100000
                ? `Rs ${filters.rentRange[0].toLocaleString()} - ${filters.rentRange[1].toLocaleString()}`
                : "Price Range"
            }
            sx={{ flexShrink: 0, height: 40, px: 0.5, fontSize: 14 }}
          />
          <Popover
            open={Boolean(priceAnchorEl)}
            anchorEl={priceAnchorEl}
            onClose={() => setPriceAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 3, width: 280 }}>
              <Typography gutterBottom fontWeight={600}>
                Rent Range: Rs {filters.rentRange[0].toLocaleString()} - Rs{" "}
                {filters.rentRange[1].toLocaleString()}
              </Typography>
              <Slider
                value={filters.rentRange}
                onChange={(e, newValue) =>
                  handleFilterChange("rentRange", newValue)
                }
                valueLabelDisplay="auto"
                min={0}
                max={200000}
                step={5000}
              />
            </Box>
          </Popover>

          {/* Pets Allowed - single click toggle instead of a checkbox behind a drawer */}
          <Chip
            clickable
            icon={<PetsIcon />}
            label="Pets Allowed"
            variant={filters.petsAllowed ? "filled" : "outlined"}
            color={filters.petsAllowed ? "primary" : "default"}
            onClick={() =>
              handleFilterChange("petsAllowed", !filters.petsAllowed)
            }
            sx={{ flexShrink: 0, height: 40 }}
          />

          {/* More Filters - Floor only. Location/area isn't a field we
              collect (city + address are), so it's already covered by the
              main search box instead of a dedicated filter here. */}
          <Chip
            clickable
            variant={filters.floor ? "filled" : "outlined"}
            color={filters.floor ? "primary" : "default"}
            icon={<FilterListIcon />}
            label="More Filters"
            onClick={(e) => setMoreAnchorEl(e.currentTarget)}
            sx={{ flexShrink: 0, height: 40 }}
          />
          <Popover
            open={Boolean(moreAnchorEl)}
            anchorEl={moreAnchorEl}
            onClose={() => setMoreAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 3, width: 260 }}>
              <TextField
                fullWidth
                variant="outlined"
                type="number"
                label="Floor Number"
                value={filters.floor}
                onChange={(e) => handleFilterChange("floor", e.target.value)}
                placeholder="e.g., 1, 2, 3"
                inputProps={{ min: 0 }}
              />
            </Box>
          </Popover>

          {/* Clear All - only shows once something is actually filtering */}
          {getActiveFilterCount() > 0 && (
            <Chip
              clickable
              icon={<CloseIcon />}
              label="Clear All"
              variant="outlined"
              color="error"
              onClick={handleResetFilters}
              sx={{ flexShrink: 0, height: 40 }}
            />
          )}
        </Stack>

        {/* -------- Recommended For You Section -------- */}
        {showRecommended && !search && (
          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background:
                "linear-gradient(135deg, rgba(17, 153, 142, 0.05) 0%, rgba(56, 239, 125, 0.05) 100%)",
              border: "1px solid rgba(17, 153, 142, 0.2)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ bgcolor: "#11998e", width: 40, height: 40 }}>
                  <AutoAwesomeIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Recommended For You
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on your profile preferences
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setShowRecommended(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Profile Preferences Summary */}
            {getProfileSummary().length > 0 && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                {getProfileSummary().map((item, index) => (
                  <Chip
                    key={index}
                    icon={item.icon}
                    label={item.label}
                    size="small"
                    variant="outlined"
                    sx={{ borderColor: "#11998e", color: "#11998e" }}
                  />
                ))}
              </Box>
            )}

            {/* Loading State */}
            {recommendedLoading && (
              <Box sx={{ py: 2 }}>
                <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
                <Grid container spacing={2}>
                  {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                      <Skeleton
                        variant="rectangular"
                        height={200}
                        sx={{ borderRadius: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Recommended Properties */}
            {!recommendedLoading && recommendedProperties.length > 0 && (
              <Grid container spacing={2}>
                {recommendedProperties.slice(0, 4).map((item, index) => {
                  const flat = item.property || item;
                  const matchPercentage = calculateMatchPercentage(
                    flat,
                    tenantProfile
                  );
                  const landlordInfo = {
                    id:
                      item.landlordId ||
                      item.landlord_id ||
                      flat.landlordId ||
                      flat._id,
                    name: item.landlordName || item.name || flat.landlordName,
                    email:
                      item.landlordEmail || item.email || flat.landlordEmail,
                    phone:
                      item.landlordPhone || item.phone || flat.landlordPhone,
                  };

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Card
                        sx={{
                          height: "100%",
                          maxWidth: 350,
                          mx: "auto",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 2,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          position: "relative",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                          },
                        }}
                        onClick={() => handleFlatClick(flat, landlordInfo)}
                      >
                        {/* Match Percentage Badge */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            bgcolor:
                              matchPercentage >= 80
                                ? "#4caf50"
                                : matchPercentage >= 60
                                ? "#ff9800"
                                : "#f44336",
                            color: "white",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <FavoriteIcon sx={{ fontSize: 14 }} />
                          <Typography variant="caption" fontWeight="bold">
                            {matchPercentage}% Match
                          </Typography>
                        </Box>

                        {/* Property Image */}
                        <Box sx={{ position: "relative", height: 140 }}>
                          {flat.images?.[0] ? (
                            <Box
                              component="img"
                              src={flat.images[0]}
                              alt={flat.flatName}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={cardPic}
                              alt="Property"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          )}

                          {/* Same eligibility ribbon as the search results —
                              a recommended property the tenant cannot request
                              must say so on the card itself. */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.5,
                            }}
                          >
                            {renderEligibilityChip(flat)}
                          </Box>
                        </Box>

                        <CardContent
                          sx={{
                            p: 2,
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            noWrap
                          >
                            {flat.flatName}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              mb: 1,
                            }}
                          >
                            <LocationOnIcon
                              sx={{ fontSize: 14, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {flat.city || "N/A"}{" "}
                              {flat.area ? `- ${flat.area}` : ""}
                            </Typography>
                          </Box>

                          <Typography
                            variant="h6"
                            color="primary.main"
                            fontWeight="bold"
                          >
                            Rent {Number(flat.flatRent || 0).toLocaleString()}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              /month
                            </Typography>
                          </Typography>
                          <Typography
                            variant="h6"
                            color="primary.main"
                            fontWeight="bold"
                          >
                            Advance{" "}
                            {Number(flat.flatAdvance || 0).toLocaleString()}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            ></Typography>
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {renderAllowedForChip(flat)}
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Floor: <b>{getOrdinal(flat.flatFloor)} Floor</b>
                          </Typography>
                          {/* Facilities */}
                          {(() => {
                            const facilities = [
                              {
                                label: "Pets",
                                icon: "🐾",
                                allowed:
                                  flat.petAllowed === "true" ||
                                  flat.petAllowed === true,
                              },
                              {
                                label: "Pool",
                                icon: "🏊",
                                allowed:
                                  flat.pool === "true" || flat.pool === true,
                              },
                              {
                                label: "Parking",
                                icon: "🚗",
                                allowed:
                                  flat.parkingAvailable === "true" ||
                                  flat.parkingAvailable === true,
                              },
                              {
                                label: "Furnished",
                                icon: "🛋️",
                                allowed:
                                  flat.furnished === "true" ||
                                  flat.furnished === true,
                              },
                              {
                                label: "Bath Tub",
                                icon: "🛁",
                                allowed:
                                  flat.bathTub === "true" ||
                                  flat.bathTub === true,
                              },
                              {
                                label: "Balcony",
                                icon: "🌿",
                                allowed:
                                  flat.balcony === "true" ||
                                  flat.balcony === true,
                              },
                              {
                                label: "Elevator",
                                icon: "🛗",
                                allowed:
                                  flat.elevator === "true" ||
                                  flat.elevator === true,
                              },
                              {
                                label: "Fire Extinguisher",
                                icon: "🧯",
                                allowed:
                                  flat.fireExtinguisher === "true" ||
                                  flat.fireExtinguisher === true,
                              },
                              {
                                label: "First Aid Kit",
                                icon: "🩹",
                                allowed:
                                  flat.firstAidKit === "true" ||
                                  flat.firstAidKit === true,
                              },
                              {
                                label: "Smoke Alarm",
                                icon: "🚨",
                                allowed:
                                  flat.smokeAlarm === "true" ||
                                  flat.smokeAlarm === true,
                              },
                              {
                                label: "Wifi",
                                icon: <WifiIcon />,
                                allowed:
                                  String(flat.wifi).toLowerCase() === "true" ||
                                  flat.wifi === 1,
                              },
                              {
                                label: "GYM",
                                icon: "🏋️",
                                allowed:
                                  flat.gym === "true" || flat.gym === true,
                              },
                            ];

                            const allowedList = facilities.filter(
                              (fac) => fac.allowed
                            );
                            if (allowedList.length === 0) return null;

                            const rows = [];
                            for (
                              let idx = 0;
                              idx < allowedList.length;
                              idx += 2
                            ) {
                              rows.push(allowedList.slice(idx, idx + 2));
                            }

                            return (
                              <Box sx={{ mt: 1 }}>
                                {rows.map((row, rowIdx) => (
                                  <Box
                                    key={rowIdx}
                                    sx={{ display: "flex", gap: 1, mb: 1 }}
                                  >
                                    {row.map((facility, facIdx) => (
                                      <Box
                                        key={facIdx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.6,
                                          px: 1.4,
                                          py: 0.6,
                                          bgcolor: "rgba(76, 175, 80, 0.12)",
                                          color: "#2e7d32",
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          borderRadius: "10px",
                                          border:
                                            "1px solid rgba(46, 125, 50, 0.2)",
                                        }}
                                      >
                                        <span style={{ fontSize: "14px" }}>
                                          {facility.icon}
                                        </span>
                                        {facility.label}
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            );
                          })()}

                          {/* ===== BILL BADGE - Recommended Card ===== */}
                          {/* <BillUploadedByBadge billUploadedBy={flat.bill_uploaded_by} /> */}
                          <PropertyRatingBadge flatId={flat._id} />

                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {flat.flatRooms} Bed • {flat.flatToilet || 0} Bath •{" "}
                            {flat.flatKitchen} Kitchen
                          </Typography>

                          {/* Owner's note — always rendered, same clamp
                              either way, so a card with no note isn't
                              shorter than one that has a long one. */}
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              borderRadius: 1.5,
                              bgcolor: "rgba(0,0,0,0.03)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontStyle: flat.note ? "italic" : "normal",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {flat.note ? `"${flat.note}"` : "No message from the owner."}
                            </Typography>
                          </Box>

                          <Box sx={{ mt: 1.2 }}>
                            <RulesBadge rules={rulesForFlat(flat)} />
                          </Box>

                          {/* Pushed to the bottom regardless of how much
                              content is above it, and always rendered —
                              previously this whole area vanished for any
                              flat with no existing request, making those
                              cards visibly shorter than the rest. */}
                          <Box sx={{ mt: "auto", pt: 1.5 }}>
                            {renderActionButton(flat, landlordInfo) || (
                              <Chip
                                label="Tap card for details"
                                size="small"
                                variant="outlined"
                                sx={{ color: "text.secondary" }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* No Recommendations */}
            {!recommendedLoading && recommendedProperties.length === 0 && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  No properties match your profile preferences yet. Try
                  searching manually or update your profile preferences.
                </Typography>
              </Alert>
            )}

            {/* View More */}
            {!recommendedLoading && recommendedProperties.length > 4 && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  sx={{ borderColor: "#11998e", color: "#11998e" }}
                  onClick={() => {
                    // Apply profile filters and search
                    setShowRecommended(false);
                  }}
                >
                  View All {recommendedProperties.length} Recommendations
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* -------- Results Count -------- */}
        {!loading && filteredData.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Showing <strong>{filteredData.length}</strong>{" "}
              {filteredData.length === 1 ? "property" : "properties"}
              {getActiveFilterCount() > 0 &&
                ` with ${getActiveFilterCount()} active filter${
                  getActiveFilterCount() > 1 ? "s" : ""
                }`}
            </Typography>
          </Box>
        )}

        {/* -------- Results -------- */}
        {loading && (
          <Grid container spacing={2}>
            {renderSkeletons()}
          </Grid>
        )}

        {!loading && data.length === 0 && search.trim() !== "" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No properties found. Try a different search term.
            </Typography>
          </Box>
        )}

        {!loading && data.length > 0 && filteredData.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No properties match your filters. Try adjusting your criteria.
            </Typography>
            <Button
              variant="contained"
              onClick={handleResetFilters}
              sx={{ mt: 2 }}
            >
              Reset Filters
            </Button>
          </Box>
        )}

        <Grid container spacing={2}>
          {!loading &&
            filteredData.length > 0 &&
            filteredData.map((item, i) => {
              // Case: Single flat
              if (item.property) {
                const f = item.property;
                const landlordInfo = {
                  id:
                    item.landlordId ||
                    item.landlord_id ||
                    item.userId ||
                    item.user_id ||
                    item.ownerId ||
                    item._id ||
                    f.landlordId ||
                    f.landlord_id ||
                    f.userId ||
                    f.user_id,
                  name:
                    item.landlordName ||
                    item.landlord_name ||
                    item.name ||
                    f.landlordName ||
                    f.ownerName,
                  email:
                    item.landlordEmail ||
                    item.landlord_email ||
                    item.email ||
                    f.landlordEmail,
                  phone:
                    item.landlordPhone ||
                    item.landlord_phone ||
                    item.phone ||
                    f.landlordPhone,
                };

                return (
                  <Grid key={i} item xs={12} sm={6} md={4} lg={3.5} xl={2.3}>
                    <Card
                      sx={{
                        maxWidth: 350,
                        width: "100%",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                        transition: "all 0.25s ease-in-out",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() => handleFlatClick(f, landlordInfo)}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          height: { xs: 160, sm: 180, md: 200 },
                        }}
                      >
                        {f.images?.length > 0 ? (
                          <div className="flat-slider">
                            <ImageSlider {...sliderSettings}>
                              {f.images.map((img, imgIndex) => (
                                <Box
                                  key={imgIndex}
                                  component="img"
                                  src={img}
                                  alt={`Flat ${f.flatName} ${imgIndex}`}
                                  sx={{
                                    width: "100%",
                                    height: { xs: 160, sm: 180, md: 200 },
                                    objectFit: "cover",
                                  }}
                                />
                              ))}
                            </ImageSlider>
                          </div>
                        ) : (
                          <Box
                            component="img"
                            src={cardPic}
                            alt="No flat image"
                            sx={{
                              width: "100%",
                              height: { xs: 160, sm: 180, md: 200 },
                              objectFit: "cover",
                              filter: "brightness(0.9)",
                            }}
                          />
                        )}
                        {/* Status chip on image */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 0.5,
                          }}
                        >
                          {renderStatusChip(f)}
                          {renderEligibilityChip(f)}
                        </Box>
                      </Box>

                      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "15px", sm: "16px" }, mb: 1 }}
                        >
                          {f.flatName}
                        </Typography>

                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ color: "#222", mb: 0.5 }}
                        >
                          Rent: {Number(f.flatRent || 0).toLocaleString()}{" "}
                          <span
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#777",
                              letterSpacing: "1px",
                            }}
                          >
                            PKR
                          </span>
                        </Typography>

                        {f.flatAdvance > 0 && (
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            sx={{ color: "#222", mb: 0.5 }}
                          >
                            Advance:{" "}
                            {Number(f.flatAdvance || 0).toLocaleString()}{" "}
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#777",
                                letterSpacing: "1px",
                              }}
                            >
                              PKR
                            </span>
                          </Typography>
                        )}

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {f.city || "N/A"} {f.area ? `– ${f.area}` : ""}{" "}
                          {f.block ? `, ${f.block}` : ""}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mb: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {renderAllowedForChip(f)}
                        </Box>

                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Floor: <b>{getOrdinal(f.flatFloor)} Floor</b>
                        </Typography>

                        {/* Facilities */}
                        {(() => {
                          const facilities = [
                            {
                              label: "Pets",
                              icon: "🐾",
                              allowed:
                                String(f.petAllowed).toLowerCase() === "true" ||
                                f.petAllowed === 1,
                            },
                            {
                              label: "Pool",
                              icon: "🏊",
                              allowed:
                                String(f.pool).toLowerCase() === "true" ||
                                f.pool === 1,
                            },
                            {
                              label: "Parking",
                              icon: "🚗",
                              allowed:
                                String(f.parkingAvailable).toLowerCase() ===
                                  "true" || f.parkingAvailable === 1,
                            },
                            {
                              label: "Furnished",
                              icon: "🛋️",
                              allowed:
                                String(f.furnished).toLowerCase() === "true" ||
                                f.furnished === 1,
                            },
                            {
                              label: "Bath Tub",
                              icon: "🛁",
                              allowed:
                                String(f.bathTub).toLowerCase() === "true" ||
                                f.bathTub === 1,
                            },
                            {
                              label: "Balcony",
                              icon: "🌿",
                              allowed:
                                String(f.balcony).toLowerCase() === "true" ||
                                f.balcony === 1,
                            },
                            {
                              label: "BathTub",
                              icon: "🛁",
                              allowed:
                                String(f.bathTub).toLowerCase() === "true" ||
                                f.bathTub === 1,
                            },
                            {
                              label: "Elevator",
                              icon: "🛗",
                              allowed:
                                String(f.elevator).toLowerCase() === "true" ||
                                f.elevator === 1,
                            },
                            {
                              label: "Fire Extinguisher",
                              icon: "🧯",
                              allowed:
                                String(f.fireExtinguisher).toLowerCase() ===
                                  "true" || f.fireExtinguisher === 1,
                            },
                            {
                              label: "First Aid Kit",
                              icon: "🩹",
                              allowed:
                                String(f.firstAidKit).toLowerCase() ===
                                  "true" || f.firstAidKit === 1,
                            },
                            {
                              label: "Smoke Alarm",
                              icon: "🚨",
                              allowed:
                                String(f.smokeAlarm).toLowerCase() === "true" ||
                                f.smokeAlarm === 1,
                            },
                            {
                              label: "Wifi",
                              icon: <WifiIcon />,
                              allowed:
                                String(f.wifi).toLowerCase() === "true" ||
                                f.wifi === 1,
                            },
                            {
                              label: "GYM",
                              icon: "🏋️",
                              allowed:
                                String(f.gym).toLowerCase() === "true" ||
                                f.gym === 1,
                            },
                          ];

                          const allowedList = facilities.filter(
                            (fac) => fac.allowed
                          );
                          if (allowedList.length === 0) return null;

                          const rows = [];
                          for (
                            let idx = 0;
                            idx < allowedList.length;
                            idx += 2
                          ) {
                            rows.push(allowedList.slice(idx, idx + 2));
                          }

                          return (
                            <Box sx={{ mt: 1 }}>
                              {rows.map((row, rowIdx) => (
                                <Box
                                  key={rowIdx}
                                  sx={{ display: "flex", gap: 1, mb: 1 }}
                                >
                                  {row.map((facility, facIdx) => (
                                    <Box
                                      key={facIdx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.6,
                                        px: 1.4,
                                        py: 0.6,
                                        bgcolor: "rgba(76, 175, 80, 0.12)",
                                        color: "#2e7d32",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        borderRadius: "10px",
                                        border:
                                          "1px solid rgba(46, 125, 50, 0.2)",
                                      }}
                                    >
                                      <span style={{ fontSize: "14px" }}>
                                        {facility.icon}
                                      </span>
                                      {facility.label}
                                    </Box>
                                  ))}
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}

                        {/* ===== BILL BADGE - Single flat card ===== */}
                        {/* <BillUploadedByBadge billUploadedBy={f.bill_uploaded_by} /> */}
                        <PropertyRatingBadge flatId={f._id} />

                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {f.flatRooms} Bed • {f.flatToilet || 0} Bath •{" "}
                          {f.flatKitchen} Kitchen
                        </Typography>

                        <Box sx={{ mt: 1.2 }}>
                          <RulesBadge rules={rulesForFlat(f)} />
                        </Box>
                      </CardContent>

                      {/* Action buttons only if there's a status */}
                      {renderActionButton(f, landlordInfo) && (
                        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                          {renderActionButton(f, landlordInfo)}
                        </CardActions>
                      )}
                    </Card>
                  </Grid>
                );
              }

              // Case: Multiple flats (landlord search)
              if (item.flats) {
                return item.flats.map((f, index) => {
                  const landlordInfo = {
                    id:
                      item.landlordId ||
                      item.landlord_id ||
                      item.userId ||
                      item.user_id ||
                      item.ownerId ||
                      item._id ||
                      f.landlordId ||
                      f.landlord_id ||
                      f.userId ||
                      f.user_id,
                    name:
                      item.landlordName ||
                      item.landlord_name ||
                      item.name ||
                      f.landlordName ||
                      f.ownerName,
                    email:
                      item.landlordEmail ||
                      item.landlord_email ||
                      item.email ||
                      f.landlordEmail,
                    phone:
                      item.landlordPhone ||
                      item.landlord_phone ||
                      item.phone ||
                      f.landlordPhone,
                  };

                  return (
                    <Grid
                      key={`${i}-${index}`}
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3.5}
                      xl={2.3}
                    >
                      <Card
                        sx={{
                          maxWidth: 350,
                          width: "100%",
                          borderRadius: 2,
                          overflow: "hidden",
                          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
                          transition: "all 0.25s ease-in-out",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                          },
                        }}
                        onClick={() => handleFlatClick(f, landlordInfo)}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            height: { xs: 160, sm: 180, md: 200 },
                          }}
                        >
                          {f.images?.length > 0 ? (
                            <div className="flat-slider">
                              <ImageSlider {...sliderSettings}>
                                {f.images.map((img, imgIndex) => (
                                  <Box
                                    key={imgIndex}
                                    component="img"
                                    src={img}
                                    alt={`Flat ${f.flatName} ${imgIndex}`}
                                    sx={{
                                      width: "100%",
                                      height: { xs: 160, sm: 180, md: 200 },
                                      objectFit: "cover",
                                    }}
                                  />
                                ))}
                              </ImageSlider>
                            </div>
                          ) : (
                            <Box
                              component="img"
                              src={cardPic}
                              alt="No flat image"
                              sx={{
                                width: "100%",
                                height: { xs: 160, sm: 180, md: 200 },
                                objectFit: "cover",
                                filter: "brightness(0.9)",
                              }}
                            />
                          )}
                          {/* Status chip on image */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.5,
                            }}
                          >
                            {renderStatusChip(f)}
                            {renderEligibilityChip(f)}
                          </Box>
                        </Box>

                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{ fontSize: { xs: "15px", sm: "16px" }, mb: 1 }}
                          >
                            {f.flatName}
                          </Typography>

                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ color: "#222", mb: 0.5 }}
                          >
                            Rent: {Number(f.flatRent || 0).toLocaleString()}{" "}
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#777",
                                letterSpacing: "1px",
                              }}
                            >
                              PKR
                            </span>
                          </Typography>

                          {f.flatAdvance > 0 && (
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ color: "#222", mb: 0.5 }}
                            >
                              Advance:{" "}
                              {Number(f.flatAdvance || 0).toLocaleString()}{" "}
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#777",
                                  letterSpacing: "1px",
                                }}
                              >
                                PKR
                              </span>
                            </Typography>
                          )}

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {f.city || "N/A"} {f.area ? `– ${f.area}` : ""}{" "}
                            {f.block ? `, ${f.block}` : ""}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {renderAllowedForChip(f)}
                          </Box>

                          {/* {f.flatMaintananceCharges > 0 && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Maintenance:{" "}
                              {Number(
                                f.flatMaintananceCharges
                              ).toLocaleString()}{" "}
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#777",
                                  letterSpacing: "1px",
                                }}
                              >
                                PKR
                              </span>
                            </Typography>
                          )} */}

                          <Typography variant="body2" sx={{ mb: 1 }}>
                            Floor: <b>{getOrdinal(f.flatFloor)} Floor</b>
                          </Typography>

                          {(() => {
                            const facilities = [
                              {
                                label: "Pets",
                                icon: "🐾",
                                allowed:
                                  String(f.petAllowed).toLowerCase() ===
                                    "true" || f.petAllowed === 1,
                              },
                              {
                                label: "Pool",
                                icon: "🏊",
                                allowed:
                                  String(f.pool).toLowerCase() === "true" ||
                                  f.pool === 1,
                              },
                              {
                                label: "Parking",
                                icon: "🚗",
                                allowed:
                                  String(f.parkingAvailable).toLowerCase() ===
                                    "true" || f.parkingAvailable === 1,
                              },
                              {
                                label: "Furnished",
                                icon: "🛋️",
                                allowed:
                                  String(f.furnished).toLowerCase() ===
                                    "true" || f.furnished === 1,
                              },
                              {
                                label: "Bath Tub",
                                icon: "🛁",
                                allowed:
                                  String(f.bathTub).toLowerCase() === "true" ||
                                  f.bathTub === 1,
                              },
                              {
                                label: "Balcony",
                                icon: "🌿",
                                allowed:
                                  String(f.balcony).toLowerCase() === "true" ||
                                  f.balcony === 1,
                              },
                              {
                                label: "BathTub",
                                icon: "🛁",
                                allowed:
                                  String(f.bathTub).toLowerCase() === "true" ||
                                  f.bathTub === 1,
                              },
                              {
                                label: "Elevator",
                                icon: "🛗",
                                allowed:
                                  String(f.elevator).toLowerCase() === "true" ||
                                  f.elevator === 1,
                              },
                              {
                                label: "Fire Extinguisher",
                                icon: "🧯",
                                allowed:
                                  String(f.fireExtinguisher).toLowerCase() ===
                                    "true" || f.fireExtinguisher === 1,
                              },
                              {
                                label: "First Aid Kit",
                                icon: "🩹",
                                allowed:
                                  String(f.firstAidKit).toLowerCase() ===
                                    "true" || f.firstAidKit === 1,
                              },
                              {
                                label: "Smoke Alarm",
                                icon: "🚨",
                                allowed:
                                  String(f.smokeAlarm).toLowerCase() ===
                                    "true" || f.smokeAlarm === 1,
                              },
                              {
                                label: "Wifi",
                                icon: <WifiIcon />,
                                allowed:
                                  String(f.wifi).toLowerCase() === "true" ||
                                  f.wifi === 1,
                              },
                              {
                                label: "GYM",
                                icon: "🏋️",
                                allowed:
                                  String(f.gym).toLowerCase() === "true" ||
                                  f.gym === 1,
                              },
                            ];

                            const allowedList = facilities.filter(
                              (fac) => fac.allowed
                            );
                            if (allowedList.length === 0) return null;

                            const rows = [];
                            for (
                              let idx = 0;
                              idx < allowedList.length;
                              idx += 2
                            ) {
                              rows.push(allowedList.slice(idx, idx + 2));
                            }

                            return (
                              <Box sx={{ mt: 1 }}>
                                {rows.map((row, rowIdx) => (
                                  <Box
                                    key={rowIdx}
                                    sx={{ display: "flex", gap: 1, mb: 1 }}
                                  >
                                    {row.map((facility, facIdx) => (
                                      <Box
                                        key={facIdx}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.6,
                                          px: 1.4,
                                          py: 0.6,
                                          bgcolor: "rgba(76, 175, 80, 0.12)",
                                          color: "#2e7d32",
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          borderRadius: "10px",
                                          border:
                                            "1px solid rgba(46, 125, 50, 0.2)",
                                        }}
                                      >
                                        <span style={{ fontSize: "14px" }}>
                                          {facility.icon}
                                        </span>
                                        {facility.label}
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            );
                          })()}

                          {/* ===== BILL BADGE - Multiple flats card ===== */}
                          {/* <BillUploadedByBadge billUploadedBy={f.bill_uploaded_by} /> */}
                          <PropertyRatingBadge flatId={f._id} />

                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {f.flatRooms} Bed • {f.flatToilet || 0} Bath •{" "}
                            {f.flatKitchen} Kitchen
                          </Typography>

                          <Box sx={{ mt: 1.2 }}>
                            <RulesBadge rules={rulesForFlat(f)} />
                          </Box>
                        </CardContent>

                        {/* Action buttons only if there's a status */}
                        {renderActionButton(f, landlordInfo) && (
                          <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                            {renderActionButton(f, landlordInfo)}
                          </CardActions>
                        )}
                      </Card>
                    </Grid>
                  );
                });
              }

              return null;
            })}
        </Grid>

        {/* Flat Detail Modal */}
        <FlatDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          flat={selectedFlat}
          landlordName={selectedLandlord.name}
          landlordEmail={selectedLandlord.email}
          landlordPhone={selectedLandlord.phone}
          landlordId={selectedLandlord.id}
          onInterested={handleInterested}
          requestStatus={getRequestStatusForFlat(
            selectedFlat?._id || selectedFlat?.id || selectedFlat?.flatId
          )}
          request={getRequestForFlat(
            selectedFlat?._id || selectedFlat?.id || selectedFlat?.flatId
          )}
        />

        {/* Interest Request Form */}
        <InterestRequestForm
          open={interestFormOpen}
          onClose={handleCloseInterestForm}
          flatData={selectedFlat}
          landlordId={selectedLandlord.id}
          landlordName={selectedLandlord.name}
          onSuccess={handleChildSubmitSuccess}
        />
      </div>
    </>
  );
}
